


import {
  defineComponent,
  computed,
  onMounted,
  reactive,
} from '@vue/composition-api';

import johaisetsuHanMastersApi from '@/apis/johaisetsu/johaisetsu_han_master';
import settouReportApi from '@/apis/johaisetsu/settouPatrol/settouReport/johaisetsu_settou_report';
import type { SettouReportIndexParams } from '@/apis/johaisetsu/settouPatrol/settouReport/johaisetsu_settou_report';
import { arrayUnique } from '@/lib/arrayUtil';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import {
  waitForJohaisetsuMasters,
  filterSettouReportSearchHanNames,
} from '@/lib/johaisetsu/johaisetsuHelper';
import { downloadBlob } from '@/lib/downloadHelper';
import { dtFormat } from '@/lib/dateHelper';
import type { RoadNameDirection } from '@/models';
import { redirectIfNoAbility } from '@/lib/abilityHelper';
import type { SettouWorkType } from '@/lib/johaisetsu/johaisetsuCommonUtil';
import {
  ALL_SETTOU_WORK_TYPES,
  ONE_DAY_IN_MILLISECONDS,
  THREE_DAYS_IN_MILLISECONDS,
  SETTOU_PATROL_DISPLAY_NAME,
  SETTOU_PROGRESS_MANAGER_DISPLAY_NAME,
  SETTOU_ONSITE_MANAGER_DISPLAY_NAME,
  SETTOU_ONSITE_MANAGEMENT_DISPLAY_NAME,
  johaisetsuAreas,
} from '@/lib/johaisetsu/johaisetsuCommonUtil';
import {
  SETTOU_REPORT_TYPE_ONSITE_MANAGEMENT,
} from '@/models/johaisetsu/settouPatrol/settouReport/johaisetsuSettouReport';
import type {
  SettouReport,
  SettouPatrolReportListState,
  SettouReportHanFilterState,
  CommonSearchParams,
} from '@/models/johaisetsu/settouPatrol/settouReport/johaisetsuSettouReport';
import { useSettouReports } from '@/components/Johaisetsu/SettouReport/composables/useSettouReports';
import reportDetailModal from '@/components/lib/reportDetailModal/index.vue';
import { useReportModal } from '@/composables/useReportModal';

export default defineComponent({
  components: {
    reportDetailModal,
  },
  setup() {
    const { route } = useRoute();

    const {
      fetchReports: fetchSettouReports,
      clearReports,
      buildRows,
    } = useSettouReports();

    const getInitialCommonSearchParams = (): CommonSearchParams => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return {
        dateFrom: new Date(now.getTime() - THREE_DAYS_IN_MILLISECONDS),
        dateTo: now,
        selectedWorkTypes: [...ALL_SETTOU_WORK_TYPES],
      };
    };

    const state = reactive<SettouPatrolReportListState>({
      isReady: false,
      isRequesting: false,
      isDownloading: false,
      settouReports: [],
      roadNames: [],
      roadNameMap: {},
      roadConditions: [],
      roadConditionMap: {},
      showErrorModal: false,
      errorBody: '',
      pager: {
        currentPage: 1,
        itemsPerPage: 50,
      },
      commonSearchParams: getInitialCommonSearchParams(),
      patrolSearchParams: {
        roadNames: [],
        directionItem: null,
        roadConditionText: null,
      },
      onsiteManagementSearchParams: {
        roadNames: [],
      },
    });

    const hanFilterState = reactive<SettouReportHanFilterState>({
      johaisetsuArea: null,
      johaisetsuHanMasters: [],
      selectedJohaisetsuHanNames: [],
    });

    const {
      reportModalState,
      openReportDetailModal,
      closeReportDetailModal,
    } = useReportModal();

    const store = useStore();
    const userState = store.state.user;

    const showWaitSpinner = computed<boolean>(() => {
      return !state.isReady || state.isRequesting;
    });

    const selectedWorkTypes = computed<SettouWorkType[]>(() => state.commonSearchParams.selectedWorkTypes);

    const hasPatrol = computed<boolean>(() =>
      selectedWorkTypes.value.includes(SETTOU_PATROL_DISPLAY_NAME),
    );

    const hasOnsite = computed<boolean>(() =>
      selectedWorkTypes.value.includes(SETTOU_PROGRESS_MANAGER_DISPLAY_NAME) ||
      selectedWorkTypes.value.includes(SETTOU_ONSITE_MANAGER_DISPLAY_NAME),
    );

    const isPatrolOnly = computed<boolean>(() => hasPatrol.value && !hasOnsite.value);

    const isOnsiteManagementOnly = computed<boolean>(() => !hasPatrol.value && hasOnsite.value);

    const isSearchable = computed<boolean>(() => selectedWorkTypes.value.length > 0);

    const isDateParamOk = computed<boolean>(() => {
      const params = state.commonSearchParams;
      const maxDateRange = 31;
      return (
        Math.abs(params.dateTo.getTime() - params.dateFrom.getTime()) <
          (maxDateRange + 1) * ONE_DAY_IN_MILLISECONDS
      );
    });

    const paginatedSettouReports = computed<SettouReport[]>(() => {
      const { currentPage, itemsPerPage } = state.pager;
      const start = (currentPage - 1) * itemsPerPage;
      return state.settouReports.slice(start, start + itemsPerPage);
    });

    const directionItems = computed<Array<{ id: string; label: string }>>(() => {
      const directions = state.patrolSearchParams.roadNames.flatMap(
        roadName => roadName.directions.map(e => e.direction),
      );
      return arrayUnique(directions, e => e).map(direction => ({ id: direction, label: direction }));
    });

    const johaisetsuHanNameChoices = computed<string[]>(() => filterSettouReportSearchHanNames(
      hanFilterState.johaisetsuHanMasters,
      selectedWorkTypes.value,
      hanFilterState.johaisetsuArea,
    ));

    const onChangeJohaisetsuArea = (): void => {
      hanFilterState.selectedJohaisetsuHanNames = [];
    };

    const partialResetSearchParams = (): void => {
      state.patrolSearchParams = {
        roadNames: [],
        directionItem: null,
        roadConditionText: null,
      };
      state.onsiteManagementSearchParams = {
        roadNames: [],
      };
    };

    const clearSearchResult = (): void => {
      clearReports();
      state.settouReports = [];
    };

    const onChangeWorkTypes = (): void => {
      partialResetSearchParams();
      hanFilterState.selectedJohaisetsuHanNames = [];
      clearSearchResult();
    };

    const fetchReports = async(): Promise<void> => {
      try {
        state.isRequesting = true;
        const tsFrom = state.commonSearchParams.dateFrom;
        const tsTo = new Date(state.commonSearchParams.dateTo.getTime() + ONE_DAY_IN_MILLISECONDS - 1);

        const roadNameReals = isPatrolOnly.value
          ? state.patrolSearchParams.roadNames.map(e => e.roadNameReal)
          : state.onsiteManagementSearchParams.roadNames.map(e => e.roadNameReal);
        await fetchSettouReports({
          tsFrom,
          tsTo,
          johaisetsuArea: hanFilterState.johaisetsuArea,
          johaisetsuHanNames: hanFilterState.selectedJohaisetsuHanNames,
          workTypes: selectedWorkTypes.value,
          roadNameReals,
          direction: state.patrolSearchParams.directionItem ?? '',
          roadCondition: state.patrolSearchParams.roadConditionText ?? '',
          roadNameMap: state.roadNameMap,
          roadConditionMap: state.roadConditionMap,
        });

        state.settouReports = buildRows();
        state.pager.currentPage = 1;
      } catch (e) {
        console.error('error', e);
      } finally {
        state.isRequesting = false;
      }
    };

    const loadJohaisetsuHanMasters = async(): Promise<void> => {
      try {
        const { data: hanMasters } = await johaisetsuHanMastersApi.index();
        hanFilterState.johaisetsuHanMasters = hanMasters;
      } catch (e) {
        console.error('error', e);
        hanFilterState.johaisetsuHanMasters = [];
      }
    };

    const getReportTypeName = (item: SettouReport): string => {
      if (item.reportType === SETTOU_REPORT_TYPE_ONSITE_MANAGEMENT) {
        return SETTOU_ONSITE_MANAGEMENT_DISPLAY_NAME;
      }
      return item.workTypeDisplay;
    };

    const downloadCSV = async(): Promise<void> => {
      try {
        state.isDownloading = true;
        const timestamp = dtFormat(new Date(), 'yyyymmdd_HHMMSS');
        const filename = `雪凍現場報告_${timestamp}.csv`;
        const params = state.commonSearchParams;
        const tsTo = new Date(params.dateTo.getTime() + ONE_DAY_IN_MILLISECONDS - 1);

        const reqParams: SettouReportIndexParams = {
          ts_from: params.dateFrom,
          ts_to: tsTo,
          work_types: params.selectedWorkTypes,
          johaisetsu_area: hanFilterState.johaisetsuArea ?? undefined,
          johaisetsu_han_names: hanFilterState.selectedJohaisetsuHanNames,
        };
        if (isPatrolOnly.value) {
          reqParams.road_name_disp = state.patrolSearchParams.roadNames.map(e => e.roadNameReal);
          reqParams.direction = state.patrolSearchParams.directionItem ?? undefined;
          reqParams.road_condition = state.patrolSearchParams.roadConditionText ?? undefined;
        } else if (isOnsiteManagementOnly.value) {
          reqParams.road_name_disp = state.onsiteManagementSearchParams.roadNames.map(e => e.roadNameReal);
        }
        const { data } = await settouReportApi.downloadCSV(reqParams);
        await downloadBlob(data, filename);
      } catch (e) {
        console.error('error', e);
        state.errorBody = 'CSVダウンロードに失敗しました。再度操作を行ってください';
        state.showErrorModal = true;
      } finally {
        state.isDownloading = false;
      }
    };

    onMounted(async(): Promise<void> => {
      await waitForUserAndMasters();
      redirectIfNoAbility(userState, route.value);

      const envElement: HTMLMetaElement | null = document.querySelector("meta[name='viewport']");
      if (envElement) {
        envElement.setAttribute(
          'content',
          'width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no',
        );
      }

      await waitForJohaisetsuMasters();
      state.roadNames = JSON.parse(JSON.stringify(window.master.roadNameDirections.filter(e => !e.isDummy)));
      state.roadNameMap = state.roadNames.reduce<Record<string, RoadNameDirection>>((acc, e) => {
        acc[e.roadNameReal] = e;
        return acc;
      }, {});
      state.roadConditionMap = window.johaisetsuMaster.settouPatrolReportRoadConditionMap;
      state.roadConditions = window.johaisetsuMaster.settouPatrolReportRoadConditions;

      await Promise.all([loadJohaisetsuHanMasters(), fetchReports()]);

      state.isReady = true;
    });

    return {
      state,
      hanFilterState,
      johaisetsuAreas,
      reportModalState,
      // computed
      directionItems,
      johaisetsuHanNameChoices,
      showWaitSpinner,
      paginatedSettouReports,
      isDateParamOk,
      isPatrolOnly,
      isOnsiteManagementOnly,
      isSearchable,
      SETTOU_PATROL_DISPLAY_NAME,
      SETTOU_PROGRESS_MANAGER_DISPLAY_NAME,
      SETTOU_ONSITE_MANAGER_DISPLAY_NAME,
      // methods
      onChangeWorkTypes,
      onChangeJohaisetsuArea,
      openReportDetailModal,
      closeReportDetailModal,
      fetchReports,
      downloadCSV,
      getReportTypeName,
      dtFormat,
    };
  },
});
